import React, { useState, useCallback, useMemo } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectTerminalHardwareShop,
  ConnectTerminalHardwareOrders,
} from "@stripe/react-connect-js";
import { LayoutSettings } from "../../../../../boxes/LayoutSettings/LayoutSettings";
import { EntityHeader } from "../../../../../widgets/EntityHeader/EntityHeader";
import { Box } from "../../../../../shared/Box/Box";
import { ConfirmModal } from "../../../../../boxes/ConfirmModal/ConfirmModal";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";
import { http } from "../../../../../services/HttpService";
import { uiNotification } from "../../../../../services/UINotificationService";
import { useCurrentAccountQuery } from "../../../../../api/queries/useAccountQuery";
import { CheckCircleIcon } from "../../../../../assets/Icons/CheckCircleIcon";
import classes from "./terminalHardware.module.scss";
import PoweredByStripeIcon from "../../../../../assets/Icons/PoweredByStripeIcon";

const TABS = {
  SHOP: "shop",
  ORDERS: "orders",
};

const SHOW_ORDERS_KEY = "hardware_store_show_orders";
const ORDER_COMPLETE_KEY = "hardware_order_complete";

const TerminalHardware = () => {
  const { data: account } = useCurrentAccountQuery();
  const isStripe = account?.paymentSystem === "stripe";

  const [activeTab, setActiveTab] = useState(() => {
    const showOrders = sessionStorage.getItem(SHOW_ORDERS_KEY);
    if (showOrders) {
      sessionStorage.removeItem(SHOW_ORDERS_KEY);
      return TABS.ORDERS;
    }
    return TABS.SHOP;
  });
  const [loadError, setLoadError] = useState(null);
  const [showOrderComplete, setShowOrderComplete] = useState(() => {
    const orderComplete = sessionStorage.getItem(ORDER_COMPLETE_KEY);
    if (orderComplete) {
      sessionStorage.removeItem(ORDER_COMPLETE_KEY);
      return true;
    }
    return false;
  });

  const stripeConnectInstance = useMemo(() => {
    if (!isStripe) return null;
    return loadConnectAndInitialize({
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret: async () => {
        try {
          const res = await http.post(
            HTTP_ENDPOINTS.stripe.terminalHardwareAccountSession(),
          );
          return res?.data?.data?.client_secret;
        } catch (e) {
          const message =
            e?.response?.data?.message ||
            "Failed to initialize hardware store. Please try again.";
          uiNotification.error(message);
          throw e;
        }
      },
    });
  }, [isStripe]);

  const handleCheckoutFinished = useCallback(() => {
    sessionStorage.setItem(SHOW_ORDERS_KEY, "true");
    sessionStorage.setItem(ORDER_COMPLETE_KEY, "true");
    setTimeout(() => {
      window.location.reload();
    }, 5000);
  }, []);

  const handleOrderCompleteClose = useCallback(() => {
    setShowOrderComplete(false);
  }, []);

  const handleLoadError = useCallback((error) => {
    // eslint-disable-next-line no-console
    console.error("Stripe Terminal Hardware load error:", error);
    setLoadError(
      "Unable to load the hardware store. Please ensure your Stripe account is fully set up and try again.",
    );
  }, []);

  if (!isStripe) {
    return (
      <LayoutSettings contentClassName={classes.root}>
        <EntityHeader title="Hardware Store" left={<PoweredByStripeIcon />} />
        <Box>
          <div className={classes.errorState}>
            <p>
              Hardware ordering is only available for accounts using Stripe as
              their payment gateway.
            </p>
          </div>
        </Box>
      </LayoutSettings>
    );
  }

  return (
    <LayoutSettings contentClassName={classes.root}>
      <EntityHeader title="Hardware Store" left={<PoweredByStripeIcon />} />
      <Box>
        {loadError ? (
          <div className={classes.errorState}>
            <p>{loadError}</p>
          </div>
        ) : (
          <>
            <div className={classes.tabs}>
              <button
                type="button"
                className={
                  activeTab === TABS.SHOP ? classes.tabActive : classes.tab
                }
                onClick={() => setActiveTab(TABS.SHOP)}
              >
                Shop Hardware
              </button>
              <button
                type="button"
                className={
                  activeTab === TABS.ORDERS ? classes.tabActive : classes.tab
                }
                onClick={() => setActiveTab(TABS.ORDERS)}
              >
                My Orders
              </button>
            </div>
            <div className={classes.componentWrapper}>
              <ConnectComponentsProvider
                connectInstance={stripeConnectInstance}
              >
                {activeTab === TABS.SHOP && (
                  <ConnectTerminalHardwareShop
                    onCheckoutFinished={handleCheckoutFinished}
                    onLoadError={handleLoadError}
                  />
                )}
                {activeTab === TABS.ORDERS && (
                  <ConnectTerminalHardwareOrders
                    onLoadError={handleLoadError}
                  />
                )}
              </ConnectComponentsProvider>
            </div>
          </>
        )}
      </Box>
      <ConfirmModal
        isOpen={showOrderComplete}
        onClose={handleOrderCompleteClose}
        onConfirm={handleOrderCompleteClose}
        confirmTitle="OK"
        title={null}
      >
        <div className={classes.orderCompleteContent}>
          <div className={classes.orderCompleteIcon}>
            <CheckCircleIcon width="64px" />
          </div>
          <p className={classes.orderCompleteMessage}>
            Your order is complete! A confirmation email with your shipping
            details is on its way.
          </p>
        </div>
      </ConfirmModal>
    </LayoutSettings>
  );
};

export default TerminalHardware;
